/**
 * Single source of truth for currency display. The backend stores
 * `tenant.currency` as an ISO-4217 string; the UI converts that to a
 * symbol (₱ / $ / € / …) at render time.
 *
 * The list is curated (PHP first since that's the primary market).
 * Unknown ISO codes fall through `currencySymbol()` as the code
 * itself — defensive, in case a tenant has a legacy value not in the
 * picker.
 */

export type CurrencyOption = {
  iso: string;
  symbol: string;
  name: string;
};

export const CURRENCIES: CurrencyOption[] = [
  { iso: "PHP", symbol: "₱", name: "Philippine Peso" },
  { iso: "USD", symbol: "$", name: "US Dollar" },
  { iso: "EUR", symbol: "€", name: "Euro" },
  { iso: "GBP", symbol: "£", name: "British Pound" },
  { iso: "AUD", symbol: "A$", name: "Australian Dollar" },
  { iso: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { iso: "JPY", symbol: "¥", name: "Japanese Yen" },
  { iso: "HKD", symbol: "HK$", name: "Hong Kong Dollar" },
  { iso: "SGD", symbol: "S$", name: "Singapore Dollar" },
  { iso: "MYR", symbol: "RM", name: "Malaysian Ringgit" },
  { iso: "THB", symbol: "฿", name: "Thai Baht" },
  { iso: "IDR", symbol: "Rp", name: "Indonesian Rupiah" },
  { iso: "VND", symbol: "₫", name: "Vietnamese Dong" },
  { iso: "KRW", symbol: "₩", name: "South Korean Won" },
  { iso: "INR", symbol: "₹", name: "Indian Rupee" },
  { iso: "TWD", symbol: "NT$", name: "New Taiwan Dollar" },
  { iso: "CNY", symbol: "¥", name: "Chinese Yuan" },
  { iso: "NZD", symbol: "NZ$", name: "New Zealand Dollar" },
  { iso: "AED", symbol: "AED", name: "UAE Dirham" },
];

const SYMBOLS: Record<string, string> = Object.fromEntries(
  CURRENCIES.map((c) => [c.iso, c.symbol]),
);

const NAMES: Record<string, string> = Object.fromEntries(
  CURRENCIES.map((c) => [c.iso, c.name]),
);

export function currencySymbol(iso: string | undefined | null): string {
  if (!iso) return "";
  const upper = iso.toUpperCase();
  return SYMBOLS[upper] ?? upper;
}

export function currencyLabel(iso: string | undefined | null): string {
  if (!iso) return "";
  const upper = iso.toUpperCase();
  const name = NAMES[upper];
  const symbol = SYMBOLS[upper];
  if (name && symbol) return `${name} (${symbol})`;
  return upper;
}

export function formatMoney(
  iso: string | undefined | null,
  amount: number | string | undefined | null,
  opts?: { fractionDigits?: number },
): string {
  const num = typeof amount === "string" ? parseFloat(amount) : amount ?? 0;
  if (Number.isNaN(num)) return `${currencySymbol(iso)}0`;
  const digits = opts?.fractionDigits ?? 2;
  return `${currencySymbol(iso)}${num.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}
